import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { businesses } from '@/mocks/businesses';
import { categories } from '@/mocks/categories';

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1);
}

function similarityScore(biz: typeof businesses[0], queryTokens: string[]): number {
  const allText = [
    biz.name,
    biz.description,
    biz.categoryName,
    biz.city,
    biz.address,
    biz.phone,
    biz.email,
    biz.country,
    biz.website,
    ...biz.openingHours.map((h) => `${h.day} ${h.hours}`),
    ...Object.values(biz.socialLinks || {}),
  ].join(' ');
  const tokens = tokenize(allText);
  let score = 0;
  for (const q of queryTokens) {
    if (tokenize(biz.name).some((t) => t.includes(q) || q.includes(t))) score += 5;
    if (tokenize(biz.categoryName).some((t) => t.includes(q) || q.includes(t))) score += 3;
    if (tokenize(biz.city).some((t) => t.includes(q) || q.includes(t))) score += 2;
    if (score === 0 && tokens.some((t) => t.includes(q) || q.includes(t))) score += 1;
  }
  return score;
}

function findSimilarBusinesses(
  all: typeof businesses,
  query: string,
  excludeIds: string[],
  limit = 6,
): typeof businesses {
  const queryTokens = tokenize(query);
  if (queryTokens.length === 0) return [];
  return all
    .filter((b) => !excludeIds.includes(b.id))
    .map((b) => ({ business: b, score: similarityScore(b, queryTokens) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.business);
}

function highlightText(text: string, query: string) {
  if (!query) return text;
  const q = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${q})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-accent-500/20 text-foreground-950 rounded px-0.5">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function Directory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || '';
  const initialQuery = searchParams.get('q') || '';

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedCity, setSelectedCity] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 150);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const cities = useMemo(() => {
    const citySet = new Set(businesses.map((b) => b.city));
    return Array.from(citySet).sort();
  }, []);

  const filtered = useMemo(() => {
    const q = debouncedQuery.toLowerCase();
    return businesses.filter((b) => {
      const matchesQuery =
        !q ||
        b.name.toLowerCase().includes(q) ||
        b.description.toLowerCase().includes(q) ||
        b.categoryName.toLowerCase().includes(q) ||
        b.city.toLowerCase().includes(q) ||
        b.address.toLowerCase().includes(q) ||
        b.phone.toLowerCase().includes(q) ||
        b.email.toLowerCase().includes(q) ||
        b.country.toLowerCase().includes(q) ||
        b.website.toLowerCase().includes(q) ||
        b.openingHours.some((h) =>
          `${h.day} ${h.hours}`.toLowerCase().includes(q),
        ) ||
        Object.values(b.socialLinks || {}).some((url) => url.toLowerCase().includes(q));

      const matchesCategory =
        !selectedCategory ||
        b.categoryName.toLowerCase().replace(/[\s-]/g, '') ===
          selectedCategory.toLowerCase().replace(/[\s-]/g, '');

      const matchesCity = !selectedCity || b.city === selectedCity;
      const matchesRating = b.rating >= minRating;
      const matchesVerified = !verifiedOnly || b.isVerified;

      return matchesQuery && matchesCategory && matchesCity && matchesRating && matchesVerified;
    });
  }, [debouncedQuery, selectedCategory, selectedCity, minRating, verifiedOnly]);

  const similarBusinesses = useMemo(() => {
    if (filtered.length > 0 || debouncedQuery.length < 2) return [];
    return findSimilarBusinesses(businesses, debouncedQuery, []);
  }, [filtered.length, debouncedQuery]);

  const hasActiveFilters =
    debouncedQuery || selectedCategory || selectedCity || minRating > 0 || verifiedOnly;

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <i
            key={i}
            className={`${
              i < full
                ? 'ri-star-fill text-amber-400'
                : i === full && half
                  ? 'ri-star-half-fill text-amber-400'
                  : 'ri-star-line text-foreground-500'
            } text-xs`}
          />
        ))}
      </div>
    );
  };

  const clearAll = () => {
    setSearchQuery('');
    setDebouncedQuery('');
    setSelectedCategory('');
    setSelectedCity('');
    setMinRating(0);
    setVerifiedOnly(false);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Header */}
      <div className="pt-20 md:pt-24 pb-8 bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-2">
            Annuaire d&apos;entreprises
          </h1>
          <p className="text-sm md:text-base text-foreground-400">
            Découvrez et contactez les meilleures entreprises du Togo et d&apos;Afrique.
          </p>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 lg:px-12 pt-8">
        <AdsBanner pageKey="directory" />
      </section>

      <div className="w-full px-4 md:px-8 lg:px-12 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-72 shrink-0 space-y-5">
            {/* Search */}
            <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <label className="text-sm font-semibold text-foreground-50 mb-2 block">
                Rechercher
              </label>
              <div className="flex items-center bg-background-50 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400">
                <div className="w-10 h-10 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-foreground-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nom, catégorie, ville, téléphone..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 outline-none pr-3"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="w-9 h-9 flex items-center justify-center shrink-0 text-foreground-400 hover:text-foreground-50"
                  >
                    <i className="ri-close-line" />
                  </button>
                )}
              </div>
              {debouncedQuery && (
                <p className="text-xs text-foreground-400 mt-2">
                  {filtered.length} résultat{filtered.length !== 1 ? 's' : ''} pour « {debouncedQuery} »
                </p>
              )}
            </div>

            {/* Category */}
            <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <label className="text-sm font-semibold text-foreground-50 mb-2 block">
                Catégorie
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 py-2.5 px-3 outline-none cursor-pointer"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.slug}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* City */}
            <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <label className="text-sm font-semibold text-foreground-50 mb-2 block">
                Ville
              </label>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 py-2.5 px-3 outline-none cursor-pointer"
              >
                <option value="">Toutes les villes</option>
                {cities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            {/* Rating */}
            <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <label className="text-sm font-semibold text-foreground-50 mb-3 block">
                Note minimum
              </label>
              <div className="space-y-2">
                {[4, 3, 2, 1].map((rating) => (
                  <label key={rating} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="rating"
                      checked={minRating === rating}
                      onChange={() => setMinRating(minRating === rating ? 0 : rating)}
                      className="accent-primary-500"
                    />
                    <div className="flex items-center gap-1">
                      {renderStars(rating)}
                      <span className="text-xs text-foreground-400 ml-1">et plus</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Verified */}
            <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={(e) => setVerifiedOnly(e.target.checked)}
                  className="accent-primary-500 w-4 h-4"
                />
                <span className="text-sm text-foreground-50">Entreprises vérifiées uniquement</span>
              </label>
            </div>

            {/* Reset */}
            {hasActiveFilters && (
              <button
                onClick={clearAll}
                className="w-full py-2.5 text-sm font-medium text-primary-400 hover:text-primary-300 border border-primary-400/30 rounded-lg hover:bg-primary-500/10 transition-colors whitespace-nowrap"
              >
                Réinitialiser les filtres
              </button>
            )}
          </aside>

          {/* Results */}
          <div className="flex-1">
            <div className="mb-5 flex items-center justify-between">
              <p className="text-sm text-foreground-400">
                <span className="font-semibold text-foreground-50">{filtered.length}</span>{' '}
                entreprise{filtered.length !== 1 ? 's' : ''} trouvée{filtered.length !== 1 ? 's' : ''}
              </p>
            </div>

            {filtered.length === 0 ? (
              <>
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-100">
                    <i className="ri-search-2-line text-2xl text-secondary-400" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
                    Aucun résultat exact pour « {debouncedQuery} »
                  </h3>
                  <p className="text-sm text-foreground-400 mb-2">
                    Essayez d&apos;autres mots-clés ou réinitialisez vos filtres.
                  </p>
                  {hasActiveFilters && (
                    <button
                      onClick={clearAll}
                      className="text-sm text-primary-400 hover:text-primary-300 font-medium mb-8"
                    >
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>

                {similarBusinesses.length > 0 && (
                  <div className="mt-4">
                    <h3 className="font-heading font-bold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                      <i className="ri-sparkling-2-line text-accent-400" />
                      Entreprises similaires à votre recherche
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {similarBusinesses.map((business) => (
                        <button
                          key={business.id}
                          onClick={() => navigate(`/business/${business.slug}`)}
                          className="group text-left flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                        >
                          <img
                            src={business.logoUrl}
                            alt={business.name}
                            className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h3 className="font-heading font-semibold text-base text-foreground-50 truncate">
                                {highlightText(business.name, debouncedQuery)}
                              </h3>
                              {business.isVerified && (
                                <span className="shrink-0 flex items-center gap-0.5 text-xs text-primary-400">
                                  <i className="ri-verified-badge-line" />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-foreground-400 mb-1.5">
                              {business.categoryName} &middot; {business.city}
                            </p>
                            <div className="flex items-center gap-2 mb-2">
                              {renderStars(business.rating)}
                              <span className="text-xs text-foreground-400">
                                {business.rating} ({business.reviewCount})
                              </span>
                            </div>
                            <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                              {highlightText(business.description, debouncedQuery)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {similarBusinesses.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-foreground-400">
                      Aucune entreprise similaire trouvée non plus.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {filtered.map((business) => (
                  <button
                    key={business.id}
                    onClick={() => navigate(`/business/${business.slug}`)}
                    className="group text-left flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                  >
                    <img
                      src={business.logoUrl}
                      alt={business.name}
                      className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-heading font-semibold text-base text-foreground-50 truncate">
                          {highlightText(business.name, debouncedQuery)}
                        </h3>
                        {business.isVerified && (
                          <span className="shrink-0 flex items-center gap-0.5 text-xs text-primary-400">
                            <i className="ri-verified-badge-line" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-400 mb-1.5">
                        {business.categoryName} &middot; {business.city}
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        {renderStars(business.rating)}
                        <span className="text-xs text-foreground-400">
                          {business.rating} ({business.reviewCount})
                        </span>
                      </div>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {highlightText(business.description, debouncedQuery)}
                      </p>
                      {business.phone && (
                        <p className="text-xs text-foreground-400 mt-2 flex items-center gap-1">
                          <i className="ri-phone-line" />
                          {highlightText(business.phone, debouncedQuery)}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Directory;